import { createSignal } from "solid-js";
import type { components } from "@/shared/api/schema";

export type CurrentUser = components["schemas"]["CurrentUser"];

const STORAGE_KEY = "auth_token";

// Auth is global to the app and Solid's reactivity is independent of the
// component tree, so module-scope signals are enough -- no context provider.
const [token, setToken] = createSignal(sessionStorage.getItem(STORAGE_KEY) ?? "");

// Who the token belongs to, filled in by RequireAuth from /api/auth/me. Null
// means "not loaded yet", which is why the route guard waits for it rather than
// rendering a shell with an empty sidebar.
const [currentUser, setCurrentUser] = createSignal<CurrentUser | null>(null);

export { currentUser, setCurrentUser, token };

export function signIn(next: string) {
  sessionStorage.setItem(STORAGE_KEY, next);
  setToken(next);
}

export function signOut() {
  sessionStorage.removeItem(STORAGE_KEY);
  setToken("");
  setCurrentUser(null);
}

/**
 * Whether the signed-in user holds a permission. This decides what to offer,
 * not what to allow: every endpoint behind these screens checks for itself, so
 * a stale answer here costs a wasted click rather than access.
 */
export function can(permission: string): boolean {
  const user = currentUser();
  if (!user) return false;
  if (user.isAdmin) return true;
  return (user.permissions ?? []).includes(permission);
}

/**
 * Whether this server is configured for an optional integration. Unlike `can`,
 * being an administrator makes no difference: nobody's permissions can supply a
 * credential the server does not have.
 */
export function hasFeature(feature: string): boolean {
  return (currentUser()?.features ?? []).includes(feature);
}
